import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Ban, Download, Undo2 } from "lucide-react";
import { api, downloadCsv, formatApiError, londonDateTime } from "@/lib/api";
import { inputClass } from "@/lib/constants";

const FILTERS = ["", "active", "suppressed"];

export default function AdminNewsletter() {
  const [subs, setSubs] = useState(null);
  const [audit, setAudit] = useState([]);
  const [filter, setFilter] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    const params = filter ? { filter } : {};
    api.get("/admin/newsletter/subscribers", { params }).then((r) => setSubs(r.data)).catch((e) => toast.error(formatApiError(e)));
    api.get("/admin/newsletter/audit").then((r) => setAudit(r.data)).catch(() => {});
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const suppress = () => {
    if (!email) return;
    if (!window.confirm(`Suppress ${email}? They will not receive marketing email and cannot be accidentally re-added.`)) return;
    setBusy(true);
    api.post("/admin/newsletter/suppress", { email })
      .then(() => { toast.success("Address suppressed."); setEmail(""); load(); })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setBusy(false));
  };

  const unsuppress = (addr) => {
    if (!window.confirm(`Lift the suppression for ${addr}? This does NOT re-subscribe them — they must give fresh consent themselves.`)) return;
    api.post("/admin/newsletter/unsuppress", { email: addr })
      .then((r) => { toast.success(r.data.note || "Suppression lifted."); load(); })
      .catch((e) => toast.error(formatApiError(e)));
  };

  const doExport = () => {
    setBusy(true);
    downloadCsv("/admin/newsletter/export.csv")
      .then(() => { toast.success("Export downloaded and recorded in the audit history."); })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setBusy(false));
  };

  return (
    <div data-testid="admin-newsletter-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-serif text-2xl font-bold text-cream-text">Newsletter &amp; consent</h1>
        <button disabled={busy} onClick={doExport} className="inline-flex items-center gap-1.5 rounded-full bg-gold px-4 py-2 text-xs font-bold text-choco disabled:opacity-50" data-testid="newsletter-export-button">
          <Download className="h-3.5 w-3.5" aria-hidden="true" /> Export subscribers CSV
        </button>
      </div>

      <div className="mt-4 rounded-3xl border border-choco-line bg-choco-light p-5 text-xs text-cream-muted" data-testid="newsletter-separation-note">
        <p className="font-bold text-cream-text">Marketing email is separate from order email.</p>
        <p className="mt-1">
          Only people below with an active consent receive marketing. Suppressed addresses are never mailed and cannot be re-added by forms or imports.
          Unsubscribing never blocks essential order emails, which are sent through the transactional outbox. No promotional campaigns are sent in this phase.
        </p>
      </div>

      <div className="mt-5 flex flex-wrap items-end gap-3">
        <div>
          <label htmlFor="suppress-email" className="text-[11px] font-bold uppercase tracking-widest text-cream-muted">Suppress an address</label>
          <input id="suppress-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="customer@example.co.uk" className={`${inputClass} mt-1 w-64`} data-testid="suppress-email-input" />
        </div>
        <button disabled={busy || !email} onClick={suppress} className="inline-flex items-center gap-1.5 rounded-full border border-red-500/50 px-4 py-2 text-xs font-bold text-red-300 hover:bg-red-500/10 disabled:opacity-50" data-testid="suppress-button">
          <Ban className="h-3.5 w-3.5" aria-hidden="true" /> Suppress
        </button>
      </div>

      <div className="mt-5 flex flex-wrap gap-2" data-testid="newsletter-filters">
        {FILTERS.map((f) => (
          <button key={f || "all"} onClick={() => setFilter(f)}
            className={`rounded-full px-3.5 py-1.5 text-xs font-bold capitalize ${filter === f ? "bg-gold text-choco" : "border border-choco-line text-cream-muted hover:text-gold"}`}
            data-testid={`newsletter-filter-${f || "all"}`}>
            {f || "all"}
          </button>
        ))}
      </div>

      <div className="mt-5 space-y-3" data-testid="subscribers-list">
        {subs === null && <p className="text-sm text-cream-muted">Loading…</p>}
        {subs?.length === 0 && (
          <p className="rounded-3xl border border-choco-line bg-choco-light p-8 text-center text-sm text-cream-muted" data-testid="subscribers-empty">No subscribers found.</p>
        )}
        {subs?.map((s) => (
          <div key={s.id} className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-choco-line bg-choco-light p-4" data-testid={`subscriber-row-${s.email}`}>
            <div>
              <p className="text-sm font-semibold text-cream-text">
                {s.email}
                {s.suppressed ? (
                  <span className="ml-2 rounded-full bg-red-500/15 px-2.5 py-0.5 text-[11px] font-bold text-red-300" data-testid={`subscriber-status-${s.email}`}>suppressed</span>
                ) : s.confirmed ? (
                  <span className="ml-2 rounded-full bg-green-500/15 px-2.5 py-0.5 text-[11px] font-bold text-green-400" data-testid={`subscriber-status-${s.email}`}>active</span>
                ) : (
                  <span className="ml-2 rounded-full bg-cream-muted/15 px-2.5 py-0.5 text-[11px] font-bold text-cream-muted" data-testid={`subscriber-status-${s.email}`}>no consent</span>
                )}
              </p>
              <p className="mt-0.5 text-xs text-cream-muted">
                {s.consented_at_london ? `Consented ${s.consented_at_london}` : "No consent recorded"}
                {s.source ? ` · ${s.source}` : ""}
                {s.unsubscribed_at_london ? ` · Unsubscribed ${s.unsubscribed_at_london}` : ""}
              </p>
              {s.consent_version && <p className="mt-0.5 text-[11px] text-cream-muted/70">{s.consent_version}</p>}
            </div>
            {s.suppressed && (
              <button onClick={() => unsuppress(s.email)} className="inline-flex items-center gap-1.5 rounded-full border border-choco-line px-3 py-1.5 text-[11px] font-bold text-cream-text hover:border-gold hover:text-gold" data-testid={`unsuppress-${s.email}`}>
                <Undo2 className="h-3 w-3" aria-hidden="true" /> Lift suppression
              </button>
            )}
          </div>
        ))}
      </div>

      <section className="mt-8 rounded-3xl border border-choco-line bg-choco-light p-6" data-testid="newsletter-audit-section">
        <h2 className="font-serif text-lg font-semibold text-cream-text">Suppression history</h2>
        {audit.length === 0 ? (
          <p className="mt-3 text-sm text-cream-muted" data-testid="newsletter-audit-empty">No manual changes recorded yet.</p>
        ) : (
          <ul className="mt-3 space-y-1 text-xs text-cream-muted">
            {audit.map((a) => (
              <li key={a.id} data-testid={`newsletter-audit-${a.id}`}>
                <span className="font-bold capitalize text-cream-text">{a.action}</span> — {a.email} · {a.created_at_london}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
